import React from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { useNavigation } from "@react-navigation/native";

import t from "@constants/localization";
import color from "@constants/color";
import RightArrow from "@assets/svg/RightArrow";
import useHandoverVideos from "@hooks/useHandoverVideos";

const PATH = "screens/MyDeliveries/DeliveryReturn/HandoverVideoStrip";

/**
 * Handover-video status strip, date-scoped to the card it sits on. Server-driven:
 * renders nothing unless the handoverVideoEnabled datum is on AND that date has
 * deliverable crates — so it does not appear on tabs/dates with no crates.
 * States: locked (DB not reached yet) → record CTA with n/m progress → done.
 */
function HandoverVideoStrip({ date }) {
  const navigation = useNavigation();
  const { loading, enabled, reached, counts } = useHandoverVideos(date);

  if (loading || !enabled || !counts.total) {
    return null;
  }

  const allDone = counts.done >= counts.total;

  const stripStyle = !reached
    ? styles.locked
    : allDone
      ? styles.done
      : styles.pending;
  const textStyle = !reached ? styles.lockedText : styles.activeText;
  const label = !reached
    ? t(`${PATH}/locked`)
    : allDone
      ? t(`${PATH}/done`)
      : t(`${PATH}/record`);

  return (
    <TouchableOpacity
      testID="handoverVideoStrip"
      style={[styles.strip, stripStyle]}
      disabled={!reached}
      onPress={() => navigation.navigate("CrateHandoverVideos", { date })}
    >
      <Text style={[styles.label, textStyle]}>{label}</Text>
      <View style={styles.right}>
        <Text style={[styles.count, textStyle]}>
          {counts.done}/{counts.total}
        </Text>
        {reached ? <RightArrow /> : null}
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  strip: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginHorizontal: 12,
    marginTop: 8,
  },
  locked: {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
  pending: {
    backgroundColor: "rgba(228, 0, 70, 0.06)",
    borderWidth: 1,
    borderColor: color.primary,
  },
  done: {
    backgroundColor: "rgba(0, 171, 89, 0.08)",
  },
  label: {
    flex: 1,
    fontSize: 13,
    fontFamily: "NotoSansDevanagari-Bold",
  },
  lockedText: {
    color: color.lightBlack,
  },
  activeText: {
    color: color.mediumBlack,
  },
  right: {
    flexDirection: "row",
    alignItems: "center",
  },
  count: {
    fontSize: 13,
    fontFamily: "NotoSansDevanagari-Bold",
    marginRight: 6,
  },
});

export default HandoverVideoStrip;
